import { BrandLogo } from "@/components/shared/BrandLogo";
import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { Loader2, ArrowUpDown, Star, Pencil, Check, X, Save } from "lucide-react";
import { toast } from "sonner";

const TAG_STYLE = {
  "App wall": "bg-purple-50 text-purple-600 border-purple-200",
  "Purchase required": "bg-orange-50 text-orange-600 border-orange-200",
  "Advance planning": "bg-amber-50 text-amber-600 border-amber-200",
  "Razor window": "bg-red-50 text-red-600 border-red-200",
  "Tight window": "bg-yellow-50 text-yellow-600 border-yellow-200",
  "Pay-to-play": "bg-rose-50 text-rose-600 border-rose-200",
  "Complex terms": "bg-slate-100 text-slate-600 border-slate-300",
};

const FrictionBar = ({ value }) => {
  const pct = Math.min(value / 10, 1) * 100;
  const color = value >= 6 ? "bg-red-500" : value >= 4 ? "bg-amber-500" : "bg-green-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-bold ${value >= 6 ? "text-red-600" : value >= 4 ? "text-amber-600" : "text-green-600"}`}>{value}</span>
    </div>
  );
};

// Inline editable field
const EditableField = ({ label, value, field, type = "number", onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} className="flex items-center gap-1 group" data-testid={`edit-${field}`}>
        <span className="text-[10px] text-slate-500">{label}:</span>
        <span className="text-[10px] font-bold text-slate-700">{type === "boolean" ? (value ? "Yes" : "No") : value}</span>
        <Pencil className="w-2.5 h-2.5 text-slate-300 group-hover:text-sky-500 transition" />
      </button>
    );
  }

  const save = () => {
    const newVal = type === "number" ? Number(val) : type === "boolean" ? val : val;
    onSave(field, newVal);
    setEditing(false);
  };

  if (type === "boolean") {
    return (
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-slate-500">{label}:</span>
        <select value={val ? "true" : "false"} onChange={e => setVal(e.target.value === "true")} className="text-[10px] font-bold border border-sky-300 rounded px-1 py-0.5 bg-sky-50">
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
        <button onClick={save} className="text-green-500 hover:text-green-700"><Check className="w-3 h-3" /></button>
        <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] text-slate-500">{label}:</span>
      <input type="number" value={val} onChange={e => setVal(e.target.value)} className="w-14 text-[10px] font-bold border border-sky-300 rounded px-1 py-0.5 bg-sky-50" autoFocus onKeyDown={e => e.key === "Enter" && save()} />
      <button onClick={save} className="text-green-500 hover:text-green-700"><Check className="w-3 h-3" /></button>
      <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
    </div>
  );
};

export default function FrictionIndexPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("friction");
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/friction-index?sort_by=${sortBy}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [sortBy]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFieldSave = async (businessId, field, value) => {
    setSaving(businessId);
    try {
      const res = await axios.put(`${API}/admin/business/${businessId}/fields`, { fields: { [field]: value } });
      const actionId = res.data.action_id;
      toast.success(res.data.message, {
        action: actionId ? { label: "Undo", onClick: async () => {
          try { await axios.post(`${API}/admin/actions/${actionId}/undo`); toast.success("Undone"); loadData(); } catch { toast.error("Undo window expired"); }
        }} : undefined,
        duration: 5000,
      });
      loadData(); // Refresh to recalculate friction
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(null); }
  };

  if (loading && !data) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">No data</p>;

  const { summary, offers } = data;

  return (
    <div className="space-y-4" data-testid="friction-index-panel">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-slate-800">{summary.avg_friction}</p>
          <p className="text-[10px] text-slate-400">Avg Friction</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-red-600">{summary.high_friction_count}</p>
          <p className="text-[10px] text-slate-400">High Friction (6+)</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-green-600">{summary.low_friction_count}</p>
          <p className="text-[10px] text-slate-400">Low Friction (0-2)</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-slate-800">{summary.total_offers}</p>
          <p className="text-[10px] text-slate-400">Total Offers</p>
        </div>
      </div>

      {/* Top Friction Causes */}
      {summary.top_friction_causes?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {summary.top_friction_causes.map(([tag, count]) => (
            <span key={tag} className={`px-2 py-1 rounded-lg text-[10px] font-semibold border ${TAG_STYLE[tag] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
              {tag} ({count})
            </span>
          ))}
        </div>
      )}

      {/* Sort Toggle */}
      <div className="flex gap-2">
        <button onClick={() => setSortBy("friction")} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${sortBy === "friction" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid="sort-friction">
          Worst Friction
        </button>
        <button onClick={() => setSortBy("rescue")} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${sortBy === "rescue" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid="sort-rescue">
          <ArrowUpDown className="w-3 h-3 inline mr-1" />Worth Rescuing
        </button>
      </div>

      {/* Offer List */}
      <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="friction-list">
        {offers.map((o) => {
          const isExpanded = expandedId === o.business_id;
          return (
            <div
              key={o.business_id}
              className={`rounded-xl border p-3 transition-all ${o.friction_index >= 6 ? "bg-red-50/50 border-red-200" : o.friction_index >= 4 ? "bg-amber-50/30 border-amber-200" : "bg-white border-slate-200"}`}
              data-testid={`friction-row-${o.business_id}`}
            >
              <div className="flex items-start gap-3">
                {o.logo_url ? (
                  <BrandLogo name={o.name} logoUrl={o.logo_url} size="sm" />
                ) : (
                  <BrandLogo name={o.name} size="sm" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{o.name}</p>
                    <FrictionBar value={o.friction_index} />
                  </div>
                  <p className="text-[10px] text-slate-400 truncate mt-0.5">{o.offer_title}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-slate-400">${o.offer_value_est}</span>
                    <span className="text-[10px] text-slate-300">|</span>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-2.5 h-2.5 ${i <= o.ease_stars ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                      ))}
                    </div>
                    {sortBy === "rescue" && (
                      <>
                        <span className="text-[10px] text-slate-300">|</span>
                        <span className="text-[10px] font-bold text-green-600">Rescue: {o.rescue_score}</span>
                      </>
                    )}
                  </div>
                  {o.friction_tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {o.friction_tags.map((tag) => (
                        <span key={tag} className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${TAG_STYLE[tag] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Review Button */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : o.business_id)}
                  className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition flex-shrink-0 ${isExpanded ? "bg-sky-500 text-white" : "bg-sky-50 text-sky-600 border border-sky-200 hover:bg-sky-100"}`}
                  data-testid={`review-btn-${o.business_id}`}
                >
                  {saving === o.business_id ? <Loader2 className="w-3 h-3 animate-spin" /> : isExpanded ? "Close" : "Review"}
                </button>
              </div>

              {/* Inline Editor (expanded) */}
              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-testid={`editor-${o.business_id}`}>
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Edit Friction Factors</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    <EditableField label="Window (days)" value={o.offer_window_days || 7} field="offer_window_days" onSave={(f, v) => handleFieldSave(o.business_id, f, v)} />
                    <EditableField label="Min Spend ($)" value={o.min_spend || 0} field="min_spend" onSave={(f, v) => handleFieldSave(o.business_id, f, v)} />
                    <EditableField label="Requires App" value={o.offer_requires_app || false} field="offer_requires_app" type="boolean" onSave={(f, v) => handleFieldSave(o.business_id, f, v)} />
                    <EditableField label="Purchase Req" value={o.offer_purchase_required || false} field="offer_purchase_required" type="boolean" onSave={(f, v) => handleFieldSave(o.business_id, f, v)} />
                    <EditableField label="Lead Days" value={o.offer_signup_lead_days || 0} field="offer_signup_lead_days" onSave={(f, v) => handleFieldSave(o.business_id, f, v)} />
                    <EditableField label="Value ($)" value={o.offer_value_est || 0} field="offer_value_est" onSave={(f, v) => handleFieldSave(o.business_id, f, v)} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
